/**
 * FlowV2 designer context for schema conditions / LookupURL / custom forms.
 * Host-backed getters (live); do not copy onto FormModel.
 *
 * Authoring: FlowContext.ChannelType, FlowContext.ProjectID, …
 */

export const FLOW_CONTEXT_INJECT_KEY = 'flowDesignerContext';

/**
 * @param {unknown} injected  raw inject value (may be a Ref)
 * @returns {object|null}
 */
export function unwrapFlowContext(injected) {
    if (injected == null)
        return null;
    if (
        typeof injected === 'object'
        && 'value' in injected
        && (injected.__v_isRef || Object.prototype.hasOwnProperty.call(injected, 'value'))
    ) {
        return injected.value ?? null;
    }
    return injected;
}

/**
 * Live context object reading from a FlowV2 host instance.
 * @param {object} host
 * @returns {object}
 */
export function createFlowDesignerContext(host) {
    return {
        get ChannelType() {
            return host.channelConfig?.ChannelType ?? null;
        },
        get FlowMediaType() {
            return host.flowMediaType ?? host.channelConfig?.FlowMediaType ?? null;
        },
        get ProjectID() {
            return host.parent_id ?? host.ParentID ?? null;
        },
        get ClassID() {
            return host.class_id ?? null;
        },
        get MethodID() {
            return host.FlowID ?? host.flow_id ?? null;
        },
        get MethodType() {
            return host.ItemType ?? host.flow_type ?? null;
        },
        get MethodName() {
            return host.ItemName ?? host.flow_name ?? null;
        },
        get ReturnType() {
            return host.methodReturnType ?? null;
        },
    };
}

/**
 * Resolve FlowContext for condition bags / LookupURL evaluation.
 * Prefers explicit data.FlowContext, then declared flowContext prop / resolvedFlowContext,
 * then inject, then $parent walk.
 *
 * @param {object|null} control
 * @param {object|null} data
 * @returns {object|null}
 */
/**
 * Vue 3 warns when render reads an inject/data key the instance never declared.
 * Property-grid inputs evaluate FlowContext during render and do not declare these injects.
 * Plain objects (tests) still expose the keys as own properties.
 * @param {object|null} instance
 * @param {string} key
 * @returns {unknown}
 */
function readDeclaredInstanceValue(instance, key) {
    if (instance == null || typeof instance !== 'object')
        return undefined;

    // Vue component public instance. Built-ins ($ / $options) are safe to read.
    if (instance.$) {
        const opts = instance.$options || {};
        const inject = opts.inject;
        const hasInject = Array.isArray(inject)
            ? inject.includes(key)
            : !!(inject && Object.prototype.hasOwnProperty.call(inject, key));
        const hasComputed = !!(opts.computed && Object.prototype.hasOwnProperty.call(opts.computed, key));
        const props = opts.props;
        const hasProp = Array.isArray(props)
            ? props.includes(key)
            : !!(props && Object.prototype.hasOwnProperty.call(props, key));
        if (!hasInject && !hasComputed && !hasProp)
            return undefined;
        return instance[key];
    }

    if (Object.prototype.hasOwnProperty.call(instance, key))
        return instance[key];
    return undefined;
}

export function resolveFlowContext(control, data) {
    if (data && Object.prototype.hasOwnProperty.call(data, 'FlowContext') && data.FlowContext != null)
        return unwrapFlowContext(data.FlowContext);

    let cur = control;
    while (cur) {
        if (cur.$props && Object.prototype.hasOwnProperty.call(cur.$props, 'flowContext') && cur.flowContext)
            return unwrapFlowContext(cur.flowContext);

        if (cur.$options?.computed?.resolvedFlowContext) {
            const resolved = cur.resolvedFlowContext;
            if (resolved)
                return unwrapFlowContext(resolved);
        }

        const fromInject = unwrapFlowContext(readDeclaredInstanceValue(cur, '_flowContextInjected'));
        if (fromInject)
            return fromInject;

        const fromAncestor = unwrapFlowContext(readDeclaredInstanceValue(cur, '_ancestorFlowContext'));
        if (fromAncestor)
            return fromAncestor;

        cur = cur.$parent;
    }
    return null;
}
